import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  OfferRowType,
  OfferResponseRowType,
  RequestOffersParams,
} from "./types";
import { AppThunk } from "../../store";
import { getOffersApi, deleteOfferApi } from "./api";

type offersType = {
  offersState: {
    items: OfferRowType[] | null;
    total: number;
    loading: boolean;
  };
  error: string | null;
  success: string | null;
  selectedOffer: OfferRowType | null;
};

const initialState: offersType = {
  offersState: {
    items: null,
    total: 0,
    loading: false,
  },
  error: "",
  success: "",
  selectedOffer: null,
};

const offers = createSlice({
  name: "offers",
  initialState: initialState,
  reducers: {
    getOffersStart(state: offersType) {
      state.offersState.loading = true;
      state.error = null;
    },
    getOffersSuccess(
      state: offersType,
      action: PayloadAction<{
        items: OfferResponseRowType[];
        total: number;
        params: RequestOffersParams;
      }>,
    ) {
      state.offersState.items = action.payload.items.map((item) => ({
        id: item.id,
        object_eng: item.object_eng,
        validity_start: new Date(item.validity_start).toISOString(),
        customer: item.customer.name,
        discharge_port: item.discharge_port?.title || "",
        status: item.status,
        validity_end: new Date(item.validity_end).toISOString(),
      }));
      state.offersState.total = action.payload.total;
      state.offersState.loading = false;
      state.error = null;
    },
    getOffersFailure(state: offersType, action: PayloadAction<string>) {
      state.offersState.loading = false;
      state.error = action.payload;
      state.offersState.items = null;
      state.offersState.total = 0;
    },
    setSelectedOffer(state: offersType, action: PayloadAction<number | null>) {
      state.selectedOffer =
        state.offersState.items?.find((user) => user.id === action.payload) ||
        null;
    },
    deleteOfferSuccess(state: offersType, action: PayloadAction<string>) {
      state.error = null;
      state.success = action.payload;
    },
    hideWarning(state: offersType) {
      state.error = null;
      state.success = null;
    },
  },
});

export const {
  getOffersStart,
  getOffersSuccess,
  getOffersFailure,
  setSelectedOffer,
  deleteOfferSuccess,
  hideWarning,
} = offers.actions;

export default offers.reducer;

export const fetchOffers =
  (params: RequestOffersParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getOffersStart());
      const { items, total } = await getOffersApi(params);
      dispatch(getOffersSuccess({ items, total, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getOffersFailure(error));
    }
  };

export const deleteOffer =
  (params: {
    offerId: number;
    navigate?: (path: string) => void;
    params?: Record<string, any>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      await deleteOfferApi(params.offerId);
      dispatch(deleteOfferSuccess("The offer has been successfully removed"));
      if (params.params?.pageSize !== undefined) {
        dispatch(fetchOffers({ ...params.params, page: 1 }));
      } else if (params.navigate) {
        params.navigate("/offers");
      }
    } catch {
      dispatch(getOffersFailure("The offer cannot be removed"));
    }
  };
